import { useState, useEffect, useRef } from 'react';
import { CheckIcon, CloseIcon } from './Icons.jsx';
import './MatchQuestion.css';

function shuffle(list) {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function MatchQuestion({ question, answered, onSubmit }) {
  const { payload, revealed } = question;
  const isLocked = answered || Boolean(revealed);
  const [pool, setPool] = useState(() => shuffle(payload.labels));
  const [placements, setPlacements] = useState({}); // slotId -> {id, text}
  const [selectedLabelId, setSelectedLabelId] = useState(null);
  const dragLabelId = useRef(null);

  useEffect(() => {
    setPool(shuffle(payload.labels));
    setPlacements({});
    setSelectedLabelId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  const labelById = (id) => payload.labels.find((l) => l.id === id);

  // Placement is entirely local/client-side and never gated on correctness —
  // a wrong guess has to be able to stick, or this can never actually test
  // anything (it just becomes "try every combo until one sticks"). The only
  // correctness check happens once, on submit, server-side.
  const placeLabel = (slotId, labelId) => {
    if (isLocked || !labelId) return;
    const label = labelById(labelId);
    if (!label) return;

    setPlacements((prev) => {
      const next = {};
      // Drop the label from wherever it currently sits (another slot), then
      // put it in the target slot. Whatever was already in the target slot
      // (if anything) goes back to the pool.
      let bumped = null;
      for (const [sId, l] of Object.entries(prev)) {
        if (sId === slotId) {
          if (l.id !== labelId) bumped = l;
          continue;
        }
        if (l.id === labelId) continue;
        next[sId] = l;
      }
      next[slotId] = label;

      setPool((prevPool) => {
        const withoutPlaced = prevPool.filter((l) => l.id !== labelId);
        return bumped ? [...withoutPlaced, bumped] : withoutPlaced;
      });

      return next;
    });
    setSelectedLabelId(null);
  };

  const clearSlot = (slotId) => {
    if (isLocked) return;
    setPlacements((prev) => {
      const removed = prev[slotId];
      if (!removed) return prev;
      const next = { ...prev };
      delete next[slotId];
      setPool((p) => [...p, removed]);
      return next;
    });
  };

  const onSlotClick = (slotId) => {
    if (isLocked) return;
    if (selectedLabelId) placeLabel(slotId, selectedLabelId);
    else clearSlot(slotId);
  };

  const allPlaced = Object.keys(placements).length === payload.slots.length;

  const submit = () => {
    if (isLocked || !allPlaced) return;
    onSubmit({ mapping: Object.fromEntries(Object.entries(placements).map(([s, l]) => [s, l.id])) });
  };

  return (
    <div className="card stack">
      <div className="row-between">
        <span className="q-kicker">{payload.kicker || 'จับคู่ไดอะแกรม'}</span>
      </div>
      <h2>{payload.instruction}</h2>

      <div className="match-diagram">
        {payload.slots.map((slot, i) => {
          const placedLabel = placements[slot.id];
          const correctLabelId = revealed ? payload.correctMapping?.[slot.id] : null;
          const isCorrectSlot = revealed && placedLabel && placedLabel.id === correctLabelId;
          const isWrongSlot = revealed && correctLabelId && placedLabel && placedLabel.id !== correctLabelId;
          return (
            <div key={slot.id} className="match-slot-wrap">
              <div
                className={`match-slot ${placedLabel ? 'is-placed' : ''} ${isCorrectSlot ? 'is-correct' : ''} ${isWrongSlot ? 'is-wrong' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => placeLabel(slot.id, dragLabelId.current)}
                onClick={() => onSlotClick(slot.id)}
              >
                <span>{placedLabel ? placedLabel.text : slot.caption}</span>
                {!isLocked && placedLabel && (
                  <span className="match-slot-clear" aria-hidden="true">
                    <CloseIcon size={12} />
                  </span>
                )}
              </div>
              {i < payload.connectors.length && <div className="match-connector">→ {payload.connectors[i]} →</div>}
            </div>
          );
        })}
      </div>

      {!isLocked && pool.length > 0 && (
        <div className="match-pool">
          {pool.map((label) => (
            <button
              key={label.id}
              type="button"
              className={`match-chip ${selectedLabelId === label.id ? 'is-selected' : ''}`}
              draggable={!isLocked}
              onDragStart={() => (dragLabelId.current = label.id)}
              onClick={() => setSelectedLabelId((cur) => (cur === label.id ? null : label.id))}
            >
              {label.text}
            </button>
          ))}
        </div>
      )}
      {!isLocked && pool.length > 0 && (
        <p className="text-dim text-center">
          แตะป้ายเพื่อเลือก แล้วแตะช่องที่ต้องการวาง (แตะช่องที่วางไว้แล้วเพื่อเอาป้ายกลับมา)
        </p>
      )}

      {!isLocked && (
        <button className="btn btn-primary btn-block" disabled={!allPlaced} onClick={submit}>
          ยืนยันคำตอบ
        </button>
      )}

      {isLocked && !revealed && (
        <p className="text-dim text-center">
          {answered ? 'ส่งคำตอบแล้ว รอครูเฉลย...' : 'เฉลยคำตอบแล้ว — ปิดรับคำตอบข้อนี้'}
        </p>
      )}

      {revealed && payload.revealNote && (
        <p className="match-success text-center row" style={{ justifyContent: 'center', gap: '6px' }}>
          <CheckIcon size={16} />
          <span>{payload.revealNote}</span>
        </p>
      )}
    </div>
  );
}
